// src/helpers/rutaComisiones.js

import axios from "axios";

const RAW_BASE_URL =
  process.env.REACT_APP_API_BASE ||
  "https://backre-miami.us-3.evennode.com";

const BASE_URL = String(
  RAW_BASE_URL
).replace(/\/+$/, "");

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

const p = (path) =>
  `/${String(path || "").replace(
    /^\/+/,
    ""
  )}`;

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("jwt") ||
  "";

const auth = () => {
  const token = getToken();

  return token
    ? {
        headers: {
          token,
        },
      }
    : {};
};

const debug =
  String(
    process.env.REACT_APP_DEBUG_API ||
      ""
  ).toLowerCase() === "true";

const logUrl = (
  url,
  params,
  data,
  method = "GET"
) => {
  if (!debug) return;

  console.log(
    "👉 METHOD:",
    method
  );

  console.log(
    "👉 URL:",
    API.getUri({
      url,
      params,
    })
  );

  if (data) {
    console.log(
      "📦 DATA ENVIADA:",
      data
    );
  }
};

async function req(
  method,
  url,
  {
    params,
    data,
    signal,
  } = {}
) {
  logUrl(
    url,
    params,
    data,
    method
  );

  try {
    const res =
      await API.request({
        method,
        url,
        params,
        data,
        signal,
        ...auth(),
      });

    return res.data;
  } catch (err) {
    if (debug) {
      const msg =
        err?.response?.data
          ?.message ||
        err?.response?.data
          ?.error ||
        err?.message;

      console.log(
        "🧯 API ERROR:",
        msg
      );

      console.log(
        "🧯 STATUS:",
        err?.response?.status
      );

      console.log(
        "🧯 DATA:",
        err?.response?.data
      );
    }

    throw err;
  }
}

/* =========================================================
   NORMALIZADORES
   ========================================================= */

const clean = (valor) =>
  String(valor ?? "").trim();

const upper = (valor) =>
  clean(valor).toUpperCase();

const toNumber = (
  valor,
  defecto = 0
) => {
  const numero =
    Number(valor);

  return Number.isFinite(
    numero
  )
    ? numero
    : defecto;
};

const toNumberMinZero = (
  valor,
  defecto = 0
) => {
  const numero =
    toNumber(
      valor,
      defecto
    );

  return numero < 0
    ? 0
    : numero;
};

const toBool = (
  valor,
  defecto = false
) => {
  if (
    valor === undefined ||
    valor === null ||
    valor === ""
  ) {
    return defecto;
  }

  if (
    typeof valor ===
    "boolean"
  ) {
    return valor;
  }

  const texto =
    String(valor)
      .trim()
      .toLowerCase();

  if (
    texto === "true" ||
    texto === "1"
  ) {
    return true;
  }

  if (
    texto === "false" ||
    texto === "0"
  ) {
    return false;
  }

  return defecto;
};

/* =========================================================
   BONOS
   ========================================================= */

const normalizarTramosCantidad = (
  tramos
) => {
  if (
    !Array.isArray(tramos)
  ) {
    return [];
  }

  return tramos
    .map((item) => ({
      cantidad:
        Math.max(
          1,
          Math.trunc(
            toNumber(
              item?.cantidad,
              1
            )
          )
        ),

      montoUSD:
        toNumberMinZero(
          item?.montoUSD,
          0
        ),

      activo:
        toBool(
          item?.activo,
          true
        ),
    }))
    .filter(
      (item) =>
        item.cantidad > 0
    );
};

const normalizarRanking = (
  ranking
) => {
  if (
    !Array.isArray(ranking)
  ) {
    return [];
  }

  return ranking
    .map((item) => ({
      puesto:
        Math.max(
          1,
          Math.trunc(
            toNumber(
              item?.puesto,
              1
            )
          )
        ),

      montoUSD:
        toNumberMinZero(
          item?.montoUSD,
          0
        ),

      activo:
        toBool(
          item?.activo,
          true
        ),
    }))
    .filter(
      (item) =>
        item.puesto > 0
    );
};

/* =========================================================
   PAYLOAD CONFIGURACIÓN
   ========================================================= */

export const normalizarConfiguracionComisionPayload =
  (payload = {}) => {
    const src =
      payload || {};

    return {
      sede: upper(
        src.sede
      ),

      aplicaReglaProgresiva:
        toBool(
          src.aplicaReglaProgresiva,
          false
        ),

      tramoPrecioUSD:
        toNumberMinZero(
          src.tramoPrecioUSD,
          100
        ),

      porcentajeVariacion:
        toNumberMinZero(
          src.porcentajeVariacion,
          10
        ),

      bonosSemanales: {
        activo:
          toBool(
            src
              ?.bonosSemanales
              ?.activo,
            true
          ),

        acumulativos:
          toBool(
            src
              ?.bonosSemanales
              ?.acumulativos,
            false
          ),

        tramos:
          normalizarTramosCantidad(
            src
              ?.bonosSemanales
              ?.tramos
          ),
      },

      bonosMensuales: {
        activo:
          toBool(
            src
              ?.bonosMensuales
              ?.activo,
            true
          ),

        tipo:
          upper(
            src
              ?.bonosMensuales
              ?.tipo ||
              "CANTIDAD"
          ),

        acumulativos:
          toBool(
            src
              ?.bonosMensuales
              ?.acumulativos,
            false
          ),

        tramos:
          normalizarTramosCantidad(
            src
              ?.bonosMensuales
              ?.tramos
          ),

        ranking:
          normalizarRanking(
            src
              ?.bonosMensuales
              ?.ranking
          ),
      },

      periodoMensual: {
        tipo:
          upper(
            src
              ?.periodoMensual
              ?.tipo ||
              "CICLO_SEMANAS"
          ),

        diaInicio:
          Math.max(
            1,
            Math.min(
              28,
              Math.trunc(
                toNumber(
                  src
                    ?.periodoMensual
                    ?.diaInicio,
                  1
                )
              )
            )
          ),

        semanasSugeridas:
          Math.max(
            4,
            Math.min(
              5,
              Math.trunc(
                toNumber(
                  src
                    ?.periodoMensual
                    ?.semanasSugeridas,
                  4
                )
              )
            )
          ),
      },

      vigenteDesde:
        src.vigenteDesde ||
        undefined,

      vigenteHasta:
        src.vigenteHasta ||
        null,

      activo:
        toBool(
          src.activo,
          true
        ),

      observaciones:
        upper(
          src.observaciones
        ),
    };
  };

/* =========================================================
   CONFIGURACIONES
   ========================================================= */

/*
  GET /comisiones/configuraciones
*/

export async function getConfiguracionesComision({
  signal,
} = {}) {
  const data =
    await req(
      "GET",
      p(
        "comisiones/configuraciones"
      ),
      {
        signal,
      }
    );

  const configuraciones =
    Array.isArray(
      data?.configuraciones
    )
      ? data.configuraciones
      : [];

  return {
    ok:
      data?.ok !== false,

    configuraciones,

    cantidad:
      Number(
        data?.cantidad ??
          configuraciones.length
      ),
  };
}

/*
  GET
  /comisiones/configuraciones/vigente/:sede

  Puede recibir fecha opcional:
  YYYY-MM-DD
*/

export async function getConfiguracionComisionVigente(
  sede,
  {
    fecha = "",
    signal,
  } = {}
) {
  const sedeNormalizada =
    upper(sede);

  if (!sedeNormalizada) {
    return null;
  }

  const params = {};

  if (fecha) {
    params.fecha =
      fecha;
  }

  const data =
    await req(
      "GET",
      p(
        `comisiones/configuraciones/vigente/${sedeNormalizada}`
      ),
      {
        params,
        signal,
      }
    );

  return (
    data?.configuracion ||
    null
  );
}

/*
  POST /comisiones/configuraciones
*/

export async function postConfiguracionComision(
  payload,
  {
    signal,
  } = {}
) {
  const data =
    normalizarConfiguracionComisionPayload(
      payload
    );

  if (debug) {
    console.log(
      "🟢 POST CONFIG COMISIÓN:",
      data
    );
  }

  return req(
    "POST",
    p(
      "comisiones/configuraciones"
    ),
    {
      data,
      signal,
    }
  );
}

/*
  PUT /comisiones/configuraciones/:id
*/

export async function putConfiguracionComision(
  id,
  payload,
  {
    signal,
  } = {}
) {
  const configuracionId =
    clean(id);

  if (!configuracionId) {
    throw new Error(
      "La configuración no tiene ID."
    );
  }

  const data =
    normalizarConfiguracionComisionPayload(
      payload
    );

  if (debug) {
    console.log(
      "🟣 PUT CONFIG COMISIÓN:",
      configuracionId
    );

    console.log(
      "📦 PAYLOAD:",
      data
    );
  }

  return req(
    "PUT",
    p(
      `comisiones/configuraciones/${configuracionId}`
    ),
    {
      data,
      signal,
    }
  );
}

/* =========================================================
   PRODUCTOS / COMISIONES
   ========================================================= */

/*
  GET /comisiones/productos

  q es opcional.
*/

export async function getProductosComisiones({
  q = "",
  signal,
} = {}) {
  const params = {};

  const busqueda =
    clean(q);

  if (busqueda) {
    params.q =
      busqueda;
  }

  const data =
    await req(
      "GET",
      p(
        "comisiones/productos"
      ),
      {
        params,
        signal,
      }
    );

  const productos =
    Array.isArray(
      data?.productos
    )
      ? data.productos
      : [];

  return {
    ok:
      data?.ok !== false,

    productos:
      productos.map(
        (producto) => ({
          ...producto,

          comisiona:
            producto?.comisiona !==
            false,

          comisionBaseUSD:
            toNumberMinZero(
              producto?.comisionBaseUSD,
              0
            ),

          comisionMiamiUSD:
            toNumberMinZero(
              producto?.comisionMiamiUSD,
              0
            ),
        })
      ),

    cantidad:
      Number(
        data?.cantidad ??
          productos.length
      ),
  };
}

/*
  PUT /comisiones/productos/:id

  Solamente enviamos los campos de comisión.
*/

export async function putProductoComision(
  id,
  payload = {},
  {
    signal,
  } = {}
) {
  const productoId =
    clean(id);

  if (!productoId) {
    throw new Error(
      "El producto no tiene ID."
    );
  }

  const data = {};

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "comisiona"
    )
  ) {
    data.comisiona =
      toBool(
        payload.comisiona,
        true
      );
  }

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "comisionBaseUSD"
    )
  ) {
    data.comisionBaseUSD =
      toNumberMinZero(
        payload.comisionBaseUSD,
        0
      );
  }

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "comisionMiamiUSD"
    )
  ) {
    data.comisionMiamiUSD =
      toNumberMinZero(
        payload.comisionMiamiUSD,
        0
      );
  }

  if (debug) {
    console.log(
      "🟠 PUT PRODUCTO COMISIÓN:",
      productoId
    );

    console.log(
      "📦 PAYLOAD:",
      data
    );
  }

  return req(
    "PUT",
    p(
      `comisiones/productos/${productoId}`
    ),
    {
      data,
      signal,
    }
  );
}

/* =========================================================
   PENDIENTES DE VALIDAR
   ========================================================= */

/*
  GET /comisiones/pendientes

  SOLO LECTURA.
  No genera ni modifica comisiones.
*/

export async function getPendientesComisiones({
  sede = "",
  vendedor = "",
  desde = "",
  hasta = "",
  signal,
} = {}) {
  const params = {};

  const sedeNormalizada =
    upper(sede);

  if (sedeNormalizada) {
    params.sede =
      sedeNormalizada;
  }

  const vendedorId =
    clean(vendedor);

  if (vendedorId) {
    params.vendedor =
      vendedorId;
  }

  if (desde) {
    params.desde =
      clean(desde);
  }

  if (hasta) {
    params.hasta =
      clean(hasta);
  }

  const data =
    await req(
      "GET",
      p("comisiones/pendientes"),
      {
        params,
        signal,
      }
    );

  const pendientes =
    Array.isArray(
      data?.pendientes
    )
      ? data.pendientes
      : [];

  return {
    ok:
      data?.ok !== false,

    pendientes,

    cantidad:
      Number(
        data?.cantidad ??
          pendientes.length
      ),
  };
}

/* =========================================================
   COMISIONES VALIDADAS / HISTORIAL
   ========================================================= */

/*
  GET /comisiones/validadas

  Filtros opcionales:
  - sede
  - vendedor
  - nroventa
  - desde
  - hasta
  - liquidada

  SOLO LECTURA.
*/

export async function getComisionesValidadas({
  sede = "",
  vendedor = "",
  nroventa = "",
  desde = "",
  hasta = "",
  liquidada,
  signal,
} = {}) {
  const params = {};

  const sedeNormalizada =
    upper(sede);

  if (sedeNormalizada) {
    params.sede =
      sedeNormalizada;
  }

  const vendedorId =
    clean(vendedor);

  if (vendedorId) {
    params.vendedor =
      vendedorId;
  }

  if (
    nroventa !== "" &&
    nroventa !== null &&
    nroventa !== undefined
  ) {
    const numero =
      Number(nroventa);

    if (
      Number.isFinite(numero) &&
      numero > 0
    ) {
      params.nroventa =
        numero;
    }
  }

  if (desde) {
    params.desde =
      clean(desde);
  }

  if (hasta) {
    params.hasta =
      clean(hasta);
  }

  if (
    liquidada !== undefined &&
    liquidada !== null &&
    liquidada !== ""
  ) {
      params.liquidada =
        String(liquidada)
          .toLowerCase() ===
        "true";
  }

  const data =
    await req(
      "GET",
      p(
        "comisiones/validadas"
      ),
      {
        params,
        signal,
      }
    );

  const comisiones =
    Array.isArray(
      data?.comisiones
    )
      ? data.comisiones
      : [];

  return {
    ok:
      data?.ok !== false,

    comisiones,

    cantidad:
      Number(
        data?.cantidad ??
          comisiones.length
      ),

    resumen: {
      cantidadRegistros:
        Number(
          data?.resumen
            ?.cantidadRegistros ??
            comisiones.length
        ),

      cantidadProductos:
        Number(
          data?.resumen
            ?.cantidadProductos ??
            0
        ),

      cantidadVendedores:
        Number(
          data?.resumen
            ?.cantidadVendedores ??
            0
        ),

      totalComisionUSD:
        Number(
          data?.resumen
            ?.totalComisionUSD ??
            0
        ),
    },
  };
}

/* =========================================================
   RESUMEN SEMANAL / PRE-LIQUIDACIÓN
   ========================================================= */

/*
  GET /comisiones/resumen-semanal

  SOLO LECTURA.
  No crea liquidaciones.
  No modifica comisiones.
*/

export async function getResumenSemanalComisiones({
  desde = "",
  hasta = "",
  sede = "",
  signal,
} = {}) {
  const params = {};

  if (desde) {
    params.desde =
      clean(desde);
  }

  if (hasta) {
    params.hasta =
      clean(hasta);
  }

  const sedeNormalizada =
    upper(sede);

  if (sedeNormalizada) {
    params.sede =
      sedeNormalizada;
  }

  const data =
    await req(
      "GET",
      p(
        "comisiones/resumen-semanal"
      ),
      {
        params,
        signal,
      }
    );

  const resumen =
    Array.isArray(
      data?.resumen
    )
      ? data.resumen
      : [];

  return {
    ok:
      data?.ok !== false,

    periodo: {
      desde:
        data?.periodo?.desde ||
        "",

      hasta:
        data?.periodo?.hasta ||
        "",
    },

    resumen,

    totales: {
      cantidadVendedores:
        Number(
          data?.totales
            ?.cantidadVendedores ||
            0
        ),

      cantidadVentas:
        Number(
          data?.totales
            ?.cantidadVentas ||
            0
        ),

      cantidadProductos:
        Number(
          data?.totales
            ?.cantidadProductos ||
            0
        ),

      comisionProductosUSD:
        Number(
          data?.totales
            ?.comisionProductosUSD ||
            0
        ),

      bonosSemanalesUSD:
        Number(
          data?.totales
            ?.bonosSemanalesUSD ||
            0
        ),

      totalSemanaUSD:
        Number(
          data?.totales
            ?.totalSemanaUSD ||
            0
        ),
    },
  };
}

/* =========================================================
   RESUMEN MENSUAL / PRE-LIQUIDACIÓN
   ========================================================= */

/*
  GET /comisiones/resumen-mensual

  SOLO LECTURA.
  No crea liquidaciones.
  No modifica comisiones.
*/

export async function getResumenMensualComisiones({
  desde = "",
  hasta = "",
  sede = "",
  signal,
} = {}) {
  if (
    !desde ||
    !hasta
  ) {
    throw new Error(
      "Debe indicar el período mensual."
    );
  }

  const params = {
    desde:
      clean(desde),

    hasta:
      clean(hasta),
  };

  const sedeNormalizada =
    upper(sede);

  if (sedeNormalizada) {
    params.sede =
      sedeNormalizada;
  }

  const data =
    await req(
      "GET",
      p(
        "comisiones/resumen-mensual"
      ),
      {
        params,
        signal,
      }
    );

  const resumen =
    Array.isArray(
      data?.resumen
    )
      ? data.resumen
      : [];

  return {
    ok:
      data?.ok !== false,

    periodo: {
      desde:
        data?.periodo?.desde ||
        "",

      hasta:
        data?.periodo?.hasta ||
        "",

      semanas:
        Number(
          data?.periodo?.semanas ||
          0
        ),
    },

    resumen,

    totales: {
      cantidadVendedores:
        Number(
          data?.totales
            ?.cantidadVendedores ||
            0
        ),

      cantidadVentas:
        Number(
          data?.totales
            ?.cantidadVentas ||
            0
        ),

      cantidadProductos:
        Number(
          data?.totales
            ?.cantidadProductos ||
            0
        ),

      bonosMensualesUSD:
        Number(
          data?.totales
            ?.bonosMensualesUSD ||
            0
        ),
    },
  };
}

/* =========================================================
   CERRAR LIQUIDACIÓN MENSUAL
   ========================================================= */

/*
  POST /comisiones/liquidaciones/mensual

  IMPORTANTE:
  esta operación SÍ crea liquidaciones reales
  y movimientos de cuenta corriente.

  El cierre pertenece a toda la sede.
  NO se envía vendedor.
*/

export async function cerrarLiquidacionMensual({
  sede,
  periodoDesde,
  periodoHasta,
  observaciones = "",
  signal,
} = {}) {
  const sedeNormalizada =
    upper(sede);

  if (!sedeNormalizada) {
    throw new Error(
      "Debe indicar la sede."
    );
  }

  if (
    !periodoDesde ||
    !periodoHasta
  ) {
    throw new Error(
      "Debe indicar el período mensual."
    );
  }

  const data = {
    sede:
      sedeNormalizada,

    periodoDesde:
      clean(periodoDesde),

    periodoHasta:
      clean(periodoHasta),

    observaciones:
      clean(observaciones),
  };

  return req(
    "POST",
    p(
      "comisiones/liquidaciones/mensual"
    ),
    {
      data,
      signal,
    }
  );
}

/* =========================================================
   CERRAR LIQUIDACIÓN SEMANAL
   ========================================================= */

/*
  POST /comisiones/liquidaciones/semanal

  IMPORTANTE:
  esta operación SÍ crea una liquidación real
  y marca las ComisionVenta como liquidadas.
*/

export async function cerrarLiquidacionSemanal({
  vendedor,
  sede,
  periodoDesde,
  periodoHasta,
  observaciones = "",
  signal,
} = {}) {
  const vendedorId =
    clean(vendedor);

  const sedeNormalizada =
    upper(sede);

  if (!vendedorId) {
    throw new Error(
      "Debe indicar el vendedor."
    );
  }

  if (!sedeNormalizada) {
    throw new Error(
      "Debe indicar la sede."
    );
  }

  if (
    !periodoDesde ||
    !periodoHasta
  ) {
    throw new Error(
      "Debe indicar el período semanal."
    );
  }

  const data = {
    vendedor:
      vendedorId,

    sede:
      sedeNormalizada,

    periodoDesde:
      clean(periodoDesde),

    periodoHasta:
      clean(periodoHasta),

    observaciones:
      clean(observaciones),
  };

  return req(
    "POST",
    p(
      "comisiones/liquidaciones/semanal"
    ),
    {
      data,
      signal,
    }
  );
}

export async function getLiquidacionesComisiones({
  sede = "",
  vendedor = "",
  estado = "",
  tipo = "",
  desde = "",
  hasta = "",
  signal,
} = {}) {
  const params = {};

  const sedeNormalizada =
    upper(sede);

  if (sedeNormalizada) {
    params.sede =
      sedeNormalizada;
  }

  const vendedorId =
    clean(vendedor);

  if (vendedorId) {
    params.vendedor =
      vendedorId;
  }

  const estadoNormalizado =
    upper(estado);

  if (estadoNormalizado) {
    params.estado =
      estadoNormalizado;
  }

  const tipoNormalizado =
    upper(tipo);

  if (tipoNormalizado) {
    params.tipo =
      tipoNormalizado;
  }

  if (desde) {
    params.desde =
      clean(desde);
  }

  if (hasta) {
    params.hasta =
      clean(hasta);
  }

  const data =
    await req(
      "GET",
      p(
        "comisiones/liquidaciones"
      ),
      {
        params,
        signal,
      }
    );

  const liquidaciones =
    Array.isArray(
      data?.liquidaciones
    )
      ? data.liquidaciones
      : [];

  return {
    ok:
      data?.ok !== false,

    liquidaciones,

    cantidad:
      Number(
        data?.cantidad ??
          liquidaciones.length
      ),

    resumen: {
      cantidad:
        Number(
          data?.resumen
            ?.cantidad ||
            0
        ),

      totalLiquidadoUSD:
        Number(
          data?.resumen
            ?.totalLiquidadoUSD ||
            0
        ),

      totalPagadoUSD:
        Number(
          data?.resumen
            ?.totalPagadoUSD ||
            0
        ),

      saldoPendienteUSD:
        Number(
          data?.resumen
            ?.saldoPendienteUSD ||
            0
        ),
    },
  };
}

export async function registrarPagoComision({
  liquidacionId,
  montoUSD,
  fechaPago = "",
  referencia = "",
  observaciones = "",
  signal,
} = {}) {
  const id =
    clean(liquidacionId);

  if (!id) {
    throw new Error(
      "Debe indicar la liquidación."
    );
  }

  const monto =
    Number(montoUSD);

  if (
    !Number.isFinite(monto) ||
    monto <= 0
  ) {
    throw new Error(
      "El monto del pago debe ser mayor que cero."
    );
  }

  const data = {
    montoUSD:
      monto,

    referencia:
      clean(referencia),

    observaciones:
      clean(observaciones),
  };

  if (fechaPago) {
    data.fechaPago =
      clean(fechaPago);
  }

  return req(
    "POST",
    p(
      `comisiones/liquidaciones/${id}/pagos`
    ),
    {
      data,
      signal,
    }
  );
}

export async function getPagosComisiones({
  vendedor = "",
  sede = "",
  liquidacion = "",
  estado = "",
  desde = "",
  hasta = "",
  signal,
} = {}) {
  const params = {};

  const vendedorId =
    clean(vendedor);

  if (vendedorId) {
    params.vendedor =
      vendedorId;
  }

  const sedeNormalizada =
    upper(sede);

  if (sedeNormalizada) {
    params.sede =
      sedeNormalizada;
  }

  const liquidacionId =
    clean(liquidacion);

  if (liquidacionId) {
    params.liquidacion =
      liquidacionId;
  }

  const estadoNormalizado =
    upper(estado);

  if (estadoNormalizado) {
    params.estado =
      estadoNormalizado;
  }

  if (desde) {
    params.desde =
      clean(desde);
  }

  if (hasta) {
    params.hasta =
      clean(hasta);
  }

  const data =
    await req(
      "GET",
      p(
        "comisiones/pagos"
      ),
      {
        params,
        signal,
      }
    );

  const pagos =
    Array.isArray(
      data?.pagos
    )
      ? data.pagos
      : [];

  return {
    ok:
      data?.ok !== false,

    pagos,

    cantidad:
      Number(
        data?.cantidad ??
          pagos.length
      ),

    resumen: {
      cantidadPagos:
        Number(
          data?.resumen
            ?.cantidadPagos ||
            0
        ),

      totalPagadoUSD:
        Number(
          data?.resumen
            ?.totalPagadoUSD ||
            0
        ),
    },
  };
}

export async function getCuentaCorrienteComision({
  vendedor,
  sede = "",
  signal,
} = {}) {
  const vendedorId =
    clean(vendedor);

  if (!vendedorId) {
    throw new Error(
      "Debe indicar el vendedor."
    );
  }

  const params = {};

  const sedeNormalizada =
    upper(sede);

  if (sedeNormalizada) {
    params.sede =
      sedeNormalizada;
  }

  const data =
    await req(
      "GET",
      p(
        `comisiones/cuenta-corriente/${vendedorId}`
      ),
      {
        params,
        signal,
      }
    );

  const movimientos =
    Array.isArray(
      data?.movimientos
    )
      ? data.movimientos
      : [];

  return {
    ok:
      data?.ok !== false,

    vendedor:
      data?.vendedor ||
      null,

    cantidadMovimientos:
      Number(
        data?.cantidadMovimientos ??
          movimientos.length
      ),

    resumen: {
      totalLiquidacionesUSD:
        Number(
          data?.resumen
            ?.totalLiquidacionesUSD ||
            0
        ),

      totalPagosUSD:
        Number(
          data?.resumen
            ?.totalPagosUSD ||
            0
        ),

      saldoActualUSD:
        Number(
          data?.resumen
            ?.saldoActualUSD ||
            0
        ),
    },

    movimientos,
  };
}

/* =========================================================
   PREVIEW DE COMISIÓN
   ========================================================= */

/*
  GET /comisiones/preview/:nroventa

  IMPORTANTE:
  este endpoint NO guarda comisión.
*/

export async function getPreviewComisionVenta(
  nroventa,
  {
    signal,
  } = {}
) {
  const numero =
    Number(nroventa);

  if (
    !Number.isFinite(
      numero
    ) ||
    numero <= 0
  ) {
    throw new Error(
      "Número de venta inválido."
    );
  }

  return req(
    "GET",
    p(
      `comisiones/preview/${numero}`
    ),
    {
      signal,
    }
  );
}


/* =========================================================
   VALIDAR COMISIÓN DE VENTA
   ========================================================= */

/*
  POST /comisiones/validar/:nroventa

  IMPORTANTE:
  este endpoint SÍ genera los registros reales
  de comisión y validación.
*/

export async function validarComisionVenta(
  nroventa,
  {
    observaciones = "",
    signal,
  } = {}
) {
  const numero =
    Number(nroventa);

  if (
    !Number.isFinite(
      numero
    ) ||
    numero <= 0
  ) {
    throw new Error(
      "Número de venta inválido."
    );
  }

  const data = {
    observaciones:
      clean(observaciones),
  };

  return req(
    "POST",
    p(
      `comisiones/validar/${numero}`
    ),
    {
      data,
      signal,
    }
  );
}